import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import { cn } from "@/lib/utils";
import { usePortalHost } from "../../providers/portal-host";
import { Icon, type IconName } from "../icon";
import {
  TOOLTIP_GAP_OFFSET,
  TOOLTIP_SIDE_OFFSET,
  tooltipArrowClass,
  tooltipBaseLabelClass,
  tooltipDescriptionClass,
  tooltipPopupClass,
  tooltipPositionerClass,
} from "../tooltip/tooltip-styles";

export interface TooltipIconProps {
  /**
   * Primary tooltip text.
   * Also used as the accessible label for the trigger icon.
   */
  label: string;
  /** Optional secondary descriptive text */
  description?: string;
  /** Icon to display as the trigger. Defaults to circle-info. */
  icon?: IconName;
  /** Size of the trigger icon. Defaults to "sm". */
  size?: "xs" | "sm";
  /** Side of the trigger to show the tooltip */
  side?: "top" | "bottom" | "left" | "right";
  /** Alignment along the side */
  align?: "start" | "center" | "end";
  /** Prevents the tooltip from opening and dims the icon */
  disabled?: boolean;
  /** Whether to render the tooltip arrow */
  showArrow?: boolean;
  /** Data attributes for testing and integration */
  [key: `data-${string}`]: string | undefined;
}

/**
 * A standalone info icon that reveals a tooltip on hover or focus.
 * Self-contained — no separate trigger element required.
 *
 * The `label` doubles as the trigger's accessible label, so screen readers
 * announce the tooltip content when the icon is focused.
 *
 * @example
 * <TooltipIcon label="Last updated" description="Refreshes every 5 minutes." />
 * <TooltipIcon icon="circle-info" label="Why is this required?" side="right" />
 */
function TooltipIcon({
  label,
  description,
  icon = "circle-info",
  size = "sm",
  side = "top",
  align = "center",
  disabled = false,
  showArrow = true,
  ...props
}: TooltipIconProps) {
  const portalHost = usePortalHost();

  return (
    <TooltipPrimitive.Root disabled={disabled}>
      <TooltipPrimitive.Trigger
        render={
          <span
            role="img"
            tabIndex={disabled ? -1 : 0}
            aria-label={label}
            {...props}
            className={cn(
              "inline-flex items-center justify-center",
              "cursor-default transition-colors duration-200",
              "rounded-(--radius-sm)",
              "text-icon-main",
              "hover:text-primary",
              "focus-visible:text-primary",
              "focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-button-focus-ring",
              "focus-visible:ring-offset-1",
              "focus-visible:ring-offset-surface",
              disabled && "opacity-40 pointer-events-none",
            )}
          >
            <Icon name={icon} size={size} tone="inherit" />
          </span>
        }
      />
      <TooltipPrimitive.Portal container={portalHost}>
        <TooltipPrimitive.Positioner
          side={side}
          align={align}
          sideOffset={showArrow ? TOOLTIP_SIDE_OFFSET : TOOLTIP_GAP_OFFSET}
          className={tooltipPositionerClass}
        >
          <TooltipPrimitive.Popup className={tooltipPopupClass}>
            {showArrow && (
              <TooltipPrimitive.Arrow
                data-slot="tooltip-arrow"
                className={tooltipArrowClass}
              />
            )}
            <p
              className={cn(
                tooltipBaseLabelClass,
                description
                  ? "font-(number:--font-weight-semibold)"
                  : "font-(number:--font-weight-regular)",
              )}
            >
              {label}
            </p>
            {description && (
              <p className={tooltipDescriptionClass}>{description}</p>
            )}
          </TooltipPrimitive.Popup>
        </TooltipPrimitive.Positioner>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export { TooltipIcon };
